// WHSTrafficManagementPlan.tsx
// Traffic Management Plan (TMP) builder and register.
// WHS Priority 12 — highest-frequency document for MCB's ITS roadside scope.
//
// Regulatory framework:
//   Traffic Management for Works on Roads (TMWR) Code of Practice (Qld)
//   MUTCD Part 3 — Works on Roads
//   TMR Traffic and Road Use Management (TRUM) approval system
//
// All roadside works require an approved TMP before traffic control is established.
// Complex TMPs (speed reductions, lane closures on arterials) require TMR or
// Council approval prior to commencement.

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { buildDocumentHeader, buildDocumentFooter } from '../utils/documentBranding'
import {
  AlertTriangle, CheckCircle, Clock, Download, Plus, X,
  ChevronRight, FileText, MapPin, Truck, Shield, RefreshCw,
  AlertOctagon, Tag,
} from 'lucide-react'

// ── Types ──────────────────────────────────────────────────────────────────

type TMPStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'ACTIVE' | 'AMENDED' | 'EXPIRED'
type TMPClass  = 'SIMPLE' | 'STANDARD' | 'COMPLEX'
type RoadAuthority = 'TMR' | 'COUNCIL' | 'TRANSURBAN' | 'PORT' | 'OTHER'

interface TMPSection {
  id: string
  title: string
  requirement: string
  content: string
  lastUpdated: string
  updatedByName: string
  complete: boolean
}

interface TrafficManagementPlan {
  id:            string
  tmpNumber:     string
  jobId:         string
  jobNumber:     string
  jobDescription: string
  siteAddress:   string
  version:       number
  class:         TMPClass
  status:        TMPStatus
  roadAuthority: RoadAuthority
  approvalRef:   string | null
  approvedByName: string | null
  approvedAt:    string | null
  validFrom:     string | null
  validTo:       string | null
  preparedByName: string
  preparedAt:    string
  reviewDate:    string
  postedSpeed:   number    // km/h existing
  workingSpeed:  number    // km/h temporary
  lanesAffected: string[]
  tcOfficerCount: number
  nightWorks:    boolean
  sections:      TMPSection[]
}

// ── Status and class metadata ─────────────────────────────────────────────

const STATUS_META: Record<TMPStatus, { label: string; bg: string; text: string; dot: string }> = {
  DRAFT:            { label: 'Draft',              bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400'   },
  PENDING_APPROVAL: { label: 'Awaiting approval',  bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  APPROVED:         { label: 'Approved',           bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  ACTIVE:           { label: 'Active on site',     bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  AMENDED:          { label: 'Amendment pending',  bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  EXPIRED:          { label: 'Expired',            bg: 'bg-red-100',    text: 'text-red-600',    dot: 'bg-red-400'    },
}

const CLASS_META: Record<TMPClass, { label: string; desc: string; bg: string; text: string }> = {
  SIMPLE:   { label: 'Simple',   desc: 'Low-volume road, shoulder closure, no speed reduction. Self-certified.', bg: 'bg-green-50',  text: 'text-green-700' },
  STANDARD: { label: 'Standard', desc: 'Speed reduction to 60km/h, one lane closed, alternating traffic. Engineer-certified.', bg: 'bg-amber-50', text: 'text-amber-700' },
  COMPLEX:  { label: 'Complex',  desc: 'Speed reduction to 40km/h, multiple lanes, arterial road. Road authority approval required.', bg: 'bg-red-50', text: 'text-red-700' },
}

// ── Standard TMP sections (TMWR CoP Qld + MUTCD Part 3) ──────────────────

const BASE_SECTIONS: Omit<TMPSection, 'content' | 'complete' | 'lastUpdated' | 'updatedByName'>[] = [
  { id: 'tmp-s1', title: 'Site location and road classification', requirement: 'TMWR CoP Part 2, s.3.1' },
  { id: 'tmp-s2', title: 'Description of works and duration',     requirement: 'TMWR CoP Part 2, s.3.2' },
  { id: 'tmp-s3', title: 'Traffic management approach and TMP class', requirement: 'TMWR CoP s.2.4 — Simple / Standard / Complex' },
  { id: 'tmp-s4', title: 'Temporary speed zone and extent',       requirement: 'TMWR CoP s.4.2; TMR Speed Zone Policy' },
  { id: 'tmp-s5', title: 'Lane and shoulder configuration',       requirement: 'MUTCD Part 3, s.6; TMWR CoP s.4.3' },
  { id: 'tmp-s6', title: 'Traffic control device schedule',       requirement: 'MUTCD Part 3 — signs, cones, barriers, VMS' },
  { id: 'tmp-s7', title: 'Traffic controller requirements',       requirement: 'TMWR CoP s.5 — TC officer positions, qualifications, communications' },
  { id: 'tmp-s8', title: 'Working hours and environmental conditions', requirement: 'TMR Traffic Management Specification s.8' },
  { id: 'tmp-s9', title: 'Emergency access and incident procedures', requirement: 'TMWR CoP s.6.4; WHS Reg 2011 s.43' },
  { id: 'tmp-s10', title: 'Road authority approval and conditions', requirement: 'TMR TRUM approval / Council permit / MRTE' },
]

const blankSections = (): TMPSection[] =>
  BASE_SECTIONS.map(s => ({ ...s, content: '', complete: false, lastUpdated: '', updatedByName: '' }))

// ── Seed data ──────────────────────────────────────────────────────────────

const dAgo = (d: number) => new Date(Date.now() - d * 86400000).toISOString().split('T')[0]
const dFwd  = (d: number) => new Date(Date.now() + d * 86400000).toISOString().split('T')[0]
const NOW   = new Date().toISOString().split('T')[0]

const SEED_SECTIONS_COMPLETE = (who: string): TMPSection[] => [
  { id: 'tmp-s1', title: 'Site location and road classification', requirement: 'TMWR CoP Part 2, s.3.1', content: 'Sandgate Road between Earnshaw Road and Dixon Street, Banyo QLD 4014. Arterial road — TMR declared road (RoadTek District: South East). Speed environment: 60km/h posted. AADT: approx. 24,000 vpd (TMR traffic count 2024). Adjacent to Brisbane Airport precinct — elevated freight and commercial vehicle mix.', complete: true, lastUpdated: dAgo(30), updatedByName: who },
  { id: 'tmp-s2', title: 'Description of works and duration', requirement: 'TMWR CoP Part 2, s.3.2', content: 'Installation of ITS traffic signal infrastructure including: signal pole foundations (augered 600mm dia.), conduit and pit installation (longitudinal trench, northbound lane), HV/LV cable draw, signal cabinet installation, and final commissioning. Works area: CH 0+000 to CH 0+480 (northbound kerb lane). Project duration: 8 weeks commencing mobilisation date. Night shift works required for carriageway reinstatement.', complete: true, lastUpdated: dAgo(30), updatedByName: who },
  { id: 'tmp-s3', title: 'Traffic management approach and TMP class', requirement: 'TMWR CoP s.2.4 — Simple / Standard / Complex', content: 'TMP Class: COMPLEX. Basis: arterial road >10,000 AADT, speed reduction to 40km/h, full lane closure northbound, alternating traffic flow controlled by TC officers. Two traffic controllers required at all times during active works phase. Supervising Traffic Controller (STC) to be on site during setup and breakdown. TMP prepared by accredited TMP designer (Acc. No. TMP-QLD-4421). Road authority approval required from TMR District Office prior to commencement.', complete: true, lastUpdated: dAgo(28), updatedByName: who },
  { id: 'tmp-s4', title: 'Temporary speed zone and extent', requirement: 'TMWR CoP s.4.2; TMR Speed Zone Policy', content: 'Temporary speed: 40km/h. Extent: 200m south of works to 200m north of works (total 880m of reduced speed environment). Speed change: 60→40km/h via T5-20 (REDUCE SPEED) taper. End of zone: T5-23 (END ROAD WORKS) and T5-24 (END SPEED LIMIT CHANGE). Supplementary signs: T5-20A (40km/h at night) for night shift operations. All speed signs to be regulatory size per MUTCD Table 7-1.', complete: true, lastUpdated: dAgo(28), updatedByName: who },
  { id: 'tmp-s5', title: 'Lane and shoulder configuration', requirement: 'MUTCD Part 3, s.6; TMWR CoP s.4.3', content: 'Lane 1 (kerbside, northbound) CLOSED. Lane 2 (median, northbound) OPEN. Southbound: no changes. Taper: 1:16 merge taper using T-type cones at 1m spacing for 60m. Delineation: 75mm retroreflective delineators at 6m centres through work zone. Concrete jersey barriers (Type F) at zero clearance between work zone and open lane for the 120m augering section. Pedestrian detour via western footpath — accessible detour path maintained at all times.', complete: true, lastUpdated: dAgo(27), updatedByName: who },
  { id: 'tmp-s6', title: 'Traffic control device schedule', requirement: 'MUTCD Part 3 — signs, cones, barriers, VMS', content: 'Device schedule (per setup):\n• T1-1 ROAD WORKS AHEAD × 2 (advance warning)\n• T5-20 REDUCE SPEED 40 × 2\n• T1-20 WORKERS AHEAD × 4 (zone entry)\n• T2-3.1 KEEP LEFT × 2 (merge)\n• T2-1.1 LANE CLOSED × 2 (lane closure advance)\n• Traffic cones × 48 (taper + delineation)\n• Concrete jersey barriers × 14 panels (120m work zone)\n• T5-23 END ROAD WORKS × 2\n• T5-24 END SPEED LIMIT CHANGE × 2\n• VMS board × 1 (advance warning >500m south of works)\n• Arrow board × 1 (directional guidance)\n• TL-12 PILOT VEHICLE AHEAD × 2 (if convoy required)\nAll signs: retro Class 1 sheeting minimum. Cones: orange 700mm with retroreflective banding.', complete: true, lastUpdated: dAgo(26), updatedByName: who },
  { id: 'tmp-s7', title: 'Traffic controller requirements', requirement: 'TMWR CoP s.5 — TC officer positions, qualifications, communications', content: 'TC officers required: 2 (minimum). Positions: TC North (50m north of work zone) + TC South (50m south of work zone, at merge taper). Qualifications: Valid Traffic Controller Certificate of Competency (TMR-issued), current first aid. Communication: UHF radio handsets (channel 40 reserved — site channel to be confirmed at mobilisation). TC south to take control in event of communication failure (both STOP). Supervising Traffic Controller (STC): required on site during setup (min. 2 hours), available by phone during operations. Handover: written shift handover mandatory.', complete: true, lastUpdated: dAgo(25), updatedByName: who },
  { id: 'tmp-s8', title: 'Working hours and environmental conditions', requirement: 'TMR Traffic Management Specification s.8', content: 'Standard hours: 0600–1800 (daytime) Monday to Saturday. Night works permitted for carriageway reinstatement only: 2100–0500, with prior notification to TMR (48 hours) and resident letters (72 hours). Restrictions: no lane closures on declared public holidays or during Special Events (Brisbane airport event calendar to be checked weekly). Weather: no works during rainfall events — wet road delineation performance. Maximum wind speed for VMS board: 60km/h gust (manufacturer spec). Night works: additional lighting required per TMWR CoP s.8.4 — 300 lux min. at work zone.', complete: true, lastUpdated: dAgo(24), updatedByName: who },
  { id: 'tmp-s9', title: 'Emergency access and incident procedures', requirement: 'TMWR CoP s.6.4; WHS Reg 2011 s.43', content: 'Emergency services access: TC North to immediately clear path on emergency signal — STOP sign to traffic, wave emergency vehicle through, restore control after passage. Incident within work zone: stop all works, TC officers maintain traffic control. Site supervisor calls 000 first, then PM, then WHSO. All traffic control to remain in place until emergency services release the scene. Breakdown of control (TC officer): remaining officer takes STOP position at critical point. PM to arrange replacement TC officer within 30 minutes or suspend works. Road closure: if required for emergency, TC officers form full closure, PM contacts TMR 24-hour line.', complete: true, lastUpdated: dAgo(23), updatedByName: who },
  { id: 'tmp-s10', title: 'Road authority approval and conditions', requirement: 'TMR TRUM approval / Council permit / MRTE', content: 'Authority: Department of Transport and Main Roads, South East District. Approval reference: TMR-TRUM-2026-TP001-004. Approval date: pending. Conditions attached: (1) VMS board minimum 500m advance on each approach. (2) TC officers mandatory on all shifts including night. (3) Audit by TMR Traffic Inspector required before first night shift. (4) Daily traffic control inspection log to be submitted weekly to TMR Project Officer. Contact: Greg Parkinson, TMR 0417 221 983.', complete: true, lastUpdated: dAgo(22), updatedByName: who },
]

const SEED_SECTIONS_PARTIAL = (who: string): TMPSection[] => [
  { id: 'tmp-s1', title: 'Site location and road classification', requirement: 'TMWR CoP Part 2, s.3.1', content: 'Main Street and Church Street intersection, Maroochydore QLD 4558. Council-declared road — Sunshine Coast Regional Council. Posted speed: 50km/h. AADT: approx. 8,200 vpd.', complete: true, lastUpdated: dAgo(10), updatedByName: who },
  { id: 'tmp-s2', title: 'Description of works and duration', requirement: 'TMWR CoP Part 2, s.3.2', content: 'Signal pole installation, conduit and pit works at intersection approaches. Duration: 6 weeks.', complete: true, lastUpdated: dAgo(10), updatedByName: who },
  { id: 'tmp-s3', title: 'Traffic management approach and TMP class', requirement: 'TMWR CoP s.2.4 — Simple / Standard / Complex', content: 'TMP Class: STANDARD. Speed reduction to 40km/h at intersection. TC officer on each approach during works phase.', complete: true, lastUpdated: dAgo(9), updatedByName: who },
  { id: 'tmp-s4', title: 'Temporary speed zone and extent', requirement: 'TMWR CoP s.4.2; TMR Speed Zone Policy', content: '', complete: false, lastUpdated: '', updatedByName: '' },
  { id: 'tmp-s5', title: 'Lane and shoulder configuration', requirement: 'MUTCD Part 3, s.6; TMWR CoP s.4.3', content: '', complete: false, lastUpdated: '', updatedByName: '' },
  { id: 'tmp-s6', title: 'Traffic control device schedule', requirement: 'MUTCD Part 3 — signs, cones, barriers, VMS', content: '', complete: false, lastUpdated: '', updatedByName: '' },
  { id: 'tmp-s7', title: 'Traffic controller requirements', requirement: 'TMWR CoP s.5 — TC officer positions, qualifications, communications', content: '', complete: false, lastUpdated: '', updatedByName: '' },
  { id: 'tmp-s8', title: 'Working hours and environmental conditions', requirement: 'TMR Traffic Management Specification s.8', content: '', complete: false, lastUpdated: '', updatedByName: '' },
  { id: 'tmp-s9', title: 'Emergency access and incident procedures', requirement: 'TMWR CoP s.6.4; WHS Reg 2011 s.43', content: '', complete: false, lastUpdated: '', updatedByName: '' },
  { id: 'tmp-s10', title: 'Road authority approval and conditions', requirement: 'TMR TRUM approval / Council permit / MRTE', content: '', complete: false, lastUpdated: '', updatedByName: '' },
]

const SEED_TMPS: TrafficManagementPlan[] = [
  {
    id: 'tmp-001', tmpNumber: 'TMP-TP001-001',
    jobId: 'j1', jobNumber: 'TP-2026-001',
    jobDescription: 'Banyo ITS Signal Installation — Sandgate Rd',
    siteAddress: 'Sandgate Road, Banyo QLD 4014',
    version: 2, class: 'COMPLEX', status: 'ACTIVE',
    roadAuthority: 'TMR', approvalRef: 'TMR-TRUM-2026-TP001-004',
    approvedByName: 'Greg Parkinson (TMR)', approvedAt: dAgo(21),
    validFrom: dAgo(20), validTo: dFwd(35),
    preparedByName: 'Jane Smith', preparedAt: dAgo(32),
    reviewDate: dFwd(14),
    postedSpeed: 60, workingSpeed: 40,
    lanesAffected: ['Lane 1 NB — CLOSED', 'Lane 2 NB — open'],
    tcOfficerCount: 2, nightWorks: true,
    sections: SEED_SECTIONS_COMPLETE('Jane Smith'),
  },
  {
    id: 'tmp-002', tmpNumber: 'TMP-TP003-001',
    jobId: 'j3', jobNumber: 'TP-2026-003',
    jobDescription: 'Maroochydore Signal Upgrade — Main St / Church St',
    siteAddress: 'Main Street & Church Street, Maroochydore QLD 4558',
    version: 1, class: 'STANDARD', status: 'PENDING_APPROVAL',
    roadAuthority: 'COUNCIL', approvalRef: null,
    approvedByName: null, approvedAt: null,
    validFrom: null, validTo: null,
    preparedByName: 'Sarah Lee', preparedAt: dAgo(10),
    reviewDate: dFwd(60),
    postedSpeed: 50, workingSpeed: 40,
    lanesAffected: ['Left turn lane NB — CLOSED'],
    tcOfficerCount: 2, nightWorks: true,
    sections: SEED_SECTIONS_PARTIAL('Sarah Lee'),
  },
  {
    id: 'tmp-003', tmpNumber: 'TMP-TP004-001',
    jobId: 'j4', jobNumber: 'TP-2026-004',
    jobDescription: 'Logan Road Signal Works — Greenslopes',
    siteAddress: 'Logan Road, Greenslopes QLD 4120',
    version: 1, class: 'SIMPLE', status: 'DRAFT',
    roadAuthority: 'TMR', approvalRef: null,
    approvedByName: null, approvedAt: null,
    validFrom: null, validTo: null,
    preparedByName: 'Mike Walsh', preparedAt: dAgo(2),
    reviewDate: dFwd(90),
    postedSpeed: 60, workingSpeed: 60,
    lanesAffected: ['Shoulder — works zone'],
    tcOfficerCount: 1, nightWorks: false,
    sections: blankSections(),
  },
]

// ── Helpers ────────────────────────────────────────────────────────────────

const fmt = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'

// ── PDF generation ─────────────────────────────────────────────────────────

function generateTMPPDF(tmp: TrafficManagementPlan) {
  const pct = Math.round(tmp.sections.filter(s => s.complete).length / tmp.sections.length * 100)
  const now = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })

  const sectionHtml = tmp.sections.map((s, i) => `
<div class="section">
  <div class="sec-hdr">${i + 1}. ${s.title}</div>
  <div class="sec-leg">${s.requirement}</div>
  ${s.complete
    ? `<div class="sec-body">${s.content.replace(/\n/g, '<br>')}</div>
       <div class="sec-meta">Last updated: ${s.lastUpdated} · ${s.updatedByName}</div>`
    : `<div class="sec-empty">⚠ Section not yet completed.</div>`}
</div>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Traffic Management Plan — ${tmp.tmpNumber}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:20mm 22mm}
.doc-title{border-bottom:3px solid #E65100;padding-bottom:8px;margin-bottom:14px}
h2{font-size:15pt;font-weight:900;color:#1F3864;margin:0 0 2px;text-transform:uppercase}
.subtitle{font-size:9.5pt;color:#555}
.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #ccc;margin:14px 0}
.mc{padding:6px 10px;border-bottom:1px solid #eee;font-size:9pt}
.mc:nth-child(odd){background:#f7f8fa;font-weight:bold;color:#555}
.class-box{padding:10px 14px;margin:10px 0 18px;border-radius:4px;font-size:9.5pt;font-weight:bold;border-left:5px solid #E65100;background:#FFF3E0;color:#BF360C}
.section{margin-bottom:16px;break-inside:avoid}
.sec-hdr{font-size:11pt;font-weight:bold;color:#1F3864;border-left:4px solid #E65100;padding-left:8px;margin-bottom:3px}
.sec-leg{font-size:8.5pt;color:#888;font-style:italic;margin-bottom:6px}
.sec-body{background:#f9fafb;border:1px solid #e0e0e0;border-radius:3px;padding:10px 12px;font-size:10pt;line-height:1.55;white-space:pre-wrap}
.sec-meta{font-size:8pt;color:#aaa;margin-top:4px;text-align:right}
.sec-empty{background:#FFF8E1;border:1.5px dashed #F9A825;border-radius:3px;padding:10px 12px;font-size:9.5pt;color:#B45309}
.progress-bar{height:8px;background:#e0e0e0;border-radius:4px;margin:6px 0}
.progress-fill{height:100%;border-radius:4px;background:${pct === 100 ? '#4CAF50' : pct > 50 ? '#FFA726' : '#EF5350'}}
.sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:24px}
.sig-box{border:1.5px solid #999;border-radius:4px;padding:12px 14px;min-height:80px}
.sig-lbl{font-size:8.5pt;font-weight:bold;color:#555;text-transform:uppercase;margin-bottom:4px}
.sig-line{border-bottom:1px solid #555;height:28px;margin:8px 0 4px}
.sig-name{font-size:8pt;color:#777}
.valid-box{background:#E8F5E9;border:1.5px solid #388E3C;border-radius:4px;padding:8px 14px;font-size:9pt;color:#1B5E20;margin:10px 0}
@media print{body{margin:15mm}.section{break-inside:avoid}}
</style></head><body>
${buildDocumentHeader('INVOICE')}
<div class="doc-title">
  <h2>Traffic Management Plan</h2>
  <div class="subtitle">Traffic Management for Works on Roads (TMWR) Code of Practice (Qld) &mdash; MUTCD Part 3</div>
</div>

<div class="meta-grid">
  <div class="mc">TMP number</div>       <div class="mc">${tmp.tmpNumber}</div>
  <div class="mc">Job number</div>       <div class="mc">${tmp.jobNumber}</div>
  <div class="mc">Site address</div>     <div class="mc">${tmp.siteAddress}</div>
  <div class="mc">TMP class</div>        <div class="mc">${tmp.class}</div>
  <div class="mc">Road authority</div>   <div class="mc">${tmp.roadAuthority}</div>
  <div class="mc">Approval reference</div> <div class="mc">${tmp.approvalRef ?? 'Pending'}</div>
  <div class="mc">Posted speed</div>     <div class="mc">${tmp.postedSpeed} km/h</div>
  <div class="mc">Working speed</div>    <div class="mc">${tmp.workingSpeed} km/h (temporary)</div>
  <div class="mc">TC officers</div>      <div class="mc">${tmp.tcOfficerCount} on site minimum</div>
  <div class="mc">Night works</div>      <div class="mc">${tmp.nightWorks ? 'Yes — additional conditions apply' : 'No'}</div>
  <div class="mc">Prepared by</div>      <div class="mc">${tmp.preparedByName} — ${fmt(tmp.preparedAt)}</div>
  <div class="mc">Approved by</div>      <div class="mc">${tmp.approvedByName ?? 'Pending road authority approval'}</div>
  <div class="mc">Version</div>          <div class="mc">${tmp.version}.0</div>
  <div class="mc">Completion</div>
  <div class="mc">
    <div class="progress-bar"><div class="progress-fill" style="width:${pct}%"></div></div>
    ${pct}% — ${tmp.sections.filter(s => s.complete).length} of ${tmp.sections.length} sections complete
  </div>
</div>

${tmp.status === 'ACTIVE' ? `
<div class="valid-box">
  ✓ APPROVED AND ACTIVE — Valid from: ${fmt(tmp.validFrom)} to ${fmt(tmp.validTo)} · Approval ref: ${tmp.approvalRef}
</div>` : ''}

<div class="class-box">
  TMP Class: ${tmp.class} — ${CLASS_META[tmp.class].desc}
  &nbsp;|&nbsp; Lanes affected: ${tmp.lanesAffected.join(' / ')}
</div>

${sectionHtml}

<div class="sig-grid">
  <div class="sig-box">
    <div class="sig-lbl">Prepared by (TMP designer)</div>
    <div class="sig-line">${tmp.preparedByName ? '✓ Signed electronically' : ''}</div>
    <div class="sig-name">${tmp.preparedByName}</div>
    <div class="sig-name" style="margin-top:4px">Date: ${fmt(tmp.preparedAt)}</div>
  </div>
  <div class="sig-box">
    <div class="sig-lbl">Road authority approval</div>
    <div class="sig-line">${tmp.approvedByName ? '✓ Authority approval recorded' : ''}</div>
    <div class="sig-name">${tmp.approvedByName ?? 'Pending'}</div>
    <div class="sig-name" style="margin-top:4px">Date: ${tmp.approvedAt ? fmt(tmp.approvedAt) : '______ / ______ / ______'}</div>
    <div class="sig-name" style="margin-top:4px">Ref: ${tmp.approvalRef ?? '______________________'}</div>
  </div>
</div>

${buildDocumentFooter('INVOICE', tmp.tmpNumber)}
</body></html>`

  const w = window.open('', '_blank', 'width=1000,height=820')
  if (!w) { alert('Pop-up blocked — allow pop-ups to generate PDFs'); return }
  w.document.write(html)
  w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

// ── Detail panel ──────────────────────────────────────────────────────────

function TMPDetail({
  tmp, onClose, canEdit, canApprove,
  onSubmit, onApprove, onActivate, onAmend,
}: {
  tmp: TrafficManagementPlan
  onClose: () => void
  canEdit: boolean
  canApprove: boolean
  onSubmit:   () => void
  onApprove:  (ref: string) => void
  onActivate: () => void
  onAmend:    () => void
}) {
  const { user } = useAuthStore()
  const [sections, setSections]   = useState(tmp.sections)
  const [activeId, setActiveId]   = useState(sections[0]?.id ?? '')
  const [editText, setEditText]   = useState('')
  const [editing, setEditing]     = useState(false)
  const [approvalRef, setApprovalRef] = useState('')
  const [showApproveForm, setShowApproveForm] = useState(false)

  const pct         = Math.round(sections.filter(s => s.complete).length / sections.length * 100)
  const activeSec   = sections.find(s => s.id === activeId)
  const allComplete = sections.every(s => s.complete)

  const saveSection = () => {
    setSections(prev => prev.map(s => s.id === activeId
      ? { ...s, content: editText, complete: editText.trim().length > 20, lastUpdated: NOW, updatedByName: user?.fullName ?? 'User' }
      : s
    ))
    setEditing(false)
  }

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden">

      {/* Header */}
      <div className="px-4 py-3 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-mono text-gray-400">{tmp.tmpNumber}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_META[tmp.status].bg} ${STATUS_META[tmp.status].text}`}>
                {STATUS_META[tmp.status].label}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CLASS_META[tmp.class].bg} ${CLASS_META[tmp.class].text}`}>
                {tmp.class}
              </span>
              <span className="text-xs text-gray-500">v{tmp.version}</span>
            </div>
            <div className="font-semibold text-gray-900 text-sm">{tmp.jobDescription}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" />{tmp.siteAddress}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => generateTMPPDF(tmp)}
              className="btn-secondary text-xs flex items-center gap-1">
              <Download className="w-3.5 h-3.5" />PDF
            </button>
            <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
        </div>

        {/* Speed / TC summary strip */}
        <div className="flex gap-4 mt-2 text-xs text-gray-600 flex-wrap">
          <span><Truck className="w-3.5 h-3.5 inline mr-1 text-orange-500" />{tmp.postedSpeed}→{tmp.workingSpeed} km/h</span>
          <span><Shield className="w-3.5 h-3.5 inline mr-1 text-blue-500" />{tmp.tcOfficerCount} TC officer{tmp.tcOfficerCount !== 1 ? 's' : ''}</span>
          {tmp.nightWorks && <span className="text-purple-600 font-medium">◑ Night works</span>}
          {tmp.validTo && <span className="text-gray-500">Valid to: {fmt(tmp.validTo)}</span>}
        </div>

        {/* Progress bar */}
        <div className="mt-2">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-amber-400' : 'bg-red-400'}`}
              style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
            <span>{sections.filter(s => s.complete).length} of {sections.length} sections complete</span>
            <span>{pct < 100 ? `${sections.filter(s => !s.complete).length} remaining` : 'All sections complete ✓'}</span>
          </div>
        </div>
      </div>

      {/* Body — section list + editor */}
      <div className="flex flex-1 overflow-hidden">
        <div className="w-48 flex-shrink-0 border-r overflow-y-auto">
          {sections.map((s, i) => (
            <button key={s.id}
              onClick={() => { setActiveId(s.id); setEditing(false) }}
              className={`w-full text-left px-3 py-2.5 border-b flex items-center gap-2 transition-colors text-xs ${activeId === s.id ? 'bg-blue-50 border-l-2 border-l-orange-500' : 'hover:bg-gray-50'}`}>
              <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold ${s.complete ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {s.complete ? '✓' : i + 1}
              </div>
              <span className={`truncate ${activeId === s.id ? 'text-orange-600 font-medium' : 'text-gray-700'}`}>{s.title}</span>
            </button>
          ))}
        </div>

        {activeSec && (
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">{activeSec.title}</h3>
                <div className="text-xs text-gray-400 mt-0.5 italic">{activeSec.requirement}</div>
              </div>
              {canEdit && !editing && (
                <button onClick={() => { setEditText(activeSec.content); setEditing(true) }}
                  className="btn-secondary text-xs flex-shrink-0">Edit</button>
              )}
            </div>

            {!editing ? (
              activeSec.complete ? (
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {activeSec.content}
                  <div className="text-xs text-gray-400 mt-2 border-t pt-2">
                    Updated: {activeSec.lastUpdated} by {activeSec.updatedByName}
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-dashed border-amber-200 rounded-xl p-6 text-center">
                  <AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <div className="text-sm font-medium text-amber-700">Section not yet completed</div>
                  <div className="text-xs text-amber-600 mt-0.5 mb-3">Required before TMP is submitted to road authority.</div>
                  {canEdit && (
                    <button onClick={() => { setEditText(''); setEditing(true) }}
                      className="btn-primary text-xs">Complete section</button>
                  )}
                </div>
              )
            ) : (
              <div className="space-y-3">
                <textarea
                  className="input text-sm w-full h-52 resize-none"
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  placeholder="Enter the required content for this TMP section…" />
                <div className="flex gap-2">
                  <button onClick={() => setEditing(false)} className="btn-secondary text-xs">Cancel</button>
                  <button onClick={saveSection} disabled={editText.trim().length < 20}
                    className="btn-primary text-xs disabled:opacity-50">Save section</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action footer */}
      {allComplete && tmp.status === 'DRAFT' && canEdit && (
        <div className="border-t px-4 py-3 bg-green-50 flex gap-2 flex-shrink-0 items-center">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <span className="flex-1 text-xs text-green-800">All sections complete — submit to road authority for approval.</span>
          <button onClick={onSubmit} className="btn-primary text-xs">Submit for approval</button>
        </div>
      )}

      {tmp.status === 'PENDING_APPROVAL' && canApprove && !showApproveForm && (
        <div className="border-t px-4 py-3 bg-amber-50 flex gap-2 flex-shrink-0 items-center flex-wrap">
          <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="flex-1 text-xs text-amber-800">Awaiting road authority approval. Record approval once received.</span>
          <button onClick={() => setShowApproveForm(true)} className="btn-primary text-xs">Record approval</button>
        </div>
      )}

      {tmp.status === 'PENDING_APPROVAL' && showApproveForm && canApprove && (
        <div className="border-t px-4 py-3 bg-amber-50 space-y-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-700 w-32 flex-shrink-0">Approval reference</label>
            <input value={approvalRef} onChange={e => setApprovalRef(e.target.value)}
              placeholder="e.g. TMR-TRUM-2026-xxx" className="input text-xs flex-1" />
            <button onClick={() => onApprove(approvalRef)} disabled={!approvalRef.trim()}
              className="btn-primary text-xs disabled:opacity-40 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" />Confirm approved
            </button>
            <button onClick={() => setShowApproveForm(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {tmp.status === 'APPROVED' && canEdit && (
        <div className="border-t px-4 py-3 bg-blue-50 flex gap-2 flex-shrink-0 items-center">
          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="flex-1 text-xs text-blue-800">TMP approved. Activate when traffic control is established on site.</span>
          <button onClick={onActivate} className="btn-primary text-xs">Set active on site</button>
        </div>
      )}

      {tmp.status === 'ACTIVE' && canEdit && (
        <div className="border-t px-4 py-3 bg-gray-50 flex gap-2 flex-shrink-0 items-center">
          <RefreshCw className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="flex-1 text-xs text-gray-600">Scope change? Lodge an amendment with the road authority, then update this TMP.</span>
          <button onClick={onAmend} className="btn-secondary text-xs">Lodge amendment</button>
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export function WHSTrafficManagementPlan() {
  const { isTier, user } = useAuthStore()
  const canApprove = isTier('B_PM') || isTier('A_DEVELOPER')
  const canEdit    = isTier('B_PC') || isTier('C_WHSO') || canApprove

  const [tmps, setTmps]         = useState<TrafficManagementPlan[]>(SEED_TMPS)
  const [selected, setSelected] = useState<TrafficManagementPlan | null>(null)
  const [showNew, setShowNew]   = useState(false)
  const [newJobRef, setNewJobRef]   = useState('')
  const [newAddr, setNewAddr]       = useState('')
  const [newClass, setNewClass]     = useState<TMPClass>('COMPLEX')
  const [newPosted, setNewPosted]   = useState(60)
  const [newWorking, setNewWorking] = useState(40)
  const [newTC, setNewTC]           = useState(2)
  const [newNight, setNewNight]     = useState(false)
  const [newAuthority, setNewAuthority] = useState<RoadAuthority>('TMR')

  // ── Derived ──────────────────────────────────────────────────────────────
  const active  = tmps.filter(t => t.status === 'ACTIVE').length
  const pending = tmps.filter(t => t.status === 'PENDING_APPROVAL').length
  const expiring = tmps.filter(t => t.validTo && new Date(t.validTo) <= new Date(Date.now() + 14*86400000) && t.status === 'ACTIVE').length

  // ── Handlers ─────────────────────────────────────────────────────────────
  const update = (id: string, patch: Partial<TrafficManagementPlan>) => {
    setTmps(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t))
    setSelected(prev => prev?.id === id ? { ...prev, ...patch } : prev)
  }

  const handleSubmit  = (id: string) => update(id, { status: 'PENDING_APPROVAL' })
  const handleApprove = (id: string, ref: string) =>
    update(id, { status: 'APPROVED', approvalRef: ref, approvedByName: user?.fullName ?? 'PM', approvedAt: NOW })
  const handleActivate = (id: string) =>
    update(id, { status: 'ACTIVE', validFrom: NOW, validTo: dFwd(56) })
  const handleAmend = (id: string) =>
    update(id, { status: 'AMENDED', version: (tmps.find(t => t.id === id)?.version ?? 1) + 1 })

  const handleCreate = () => {
    if (!newJobRef.trim() || !newAddr.trim()) return
    const seq = String(tmps.filter(t => t.jobNumber === newJobRef.trim()).length + 1).padStart(3, '0')
    const id  = `tmp-${Date.now()}`
    const newTmp: TrafficManagementPlan = {
      id, tmpNumber: `TMP-${newJobRef.trim().replace('TP-', '').replace('-2026-', '')}-${seq}`,
      jobId: newJobRef.trim(), jobNumber: newJobRef.trim(),
      jobDescription: `${newJobRef.trim()} — Traffic Management`,
      siteAddress: newAddr.trim(),
      version: 1, class: newClass, status: 'DRAFT',
      roadAuthority: newAuthority,
      approvalRef: null, approvedByName: null, approvedAt: null,
      validFrom: null, validTo: null,
      preparedByName: user?.fullName ?? 'PM',
      preparedAt: NOW, reviewDate: dFwd(90),
      postedSpeed: newPosted, workingSpeed: newWorking,
      lanesAffected: ['To be determined'],
      tcOfficerCount: newTC, nightWorks: newNight,
      sections: blankSections(),
    }
    setTmps(prev => [...prev, newTmp])
    setSelected(newTmp)
    setShowNew(false)
    setNewJobRef(''); setNewAddr('')
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Truck className="w-5 h-5 text-orange-600" />
            Traffic Management Plans
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            TMWR Code of Practice (Qld) · MUTCD Part 3 · Required before any traffic control is established on site
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setShowNew(v => !v)} className="btn-primary text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" />New TMP
          </button>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total TMPs',    value: tmps.length, color: 'text-gray-900', sub: 'all jobs' },
          { label: 'Active on site', value: active,  color: active  > 0 ? 'text-blue-700'  : 'text-gray-400', sub: 'traffic control in place' },
          { label: 'Awaiting approval', value: pending, color: pending > 0 ? 'text-amber-700' : 'text-gray-400', sub: 'road authority review' },
          { label: 'Expiring ≤14 days',  value: expiring, color: expiring > 0 ? 'text-red-600' : 'text-gray-400', sub: 'renewal action required' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* Compliance callouts */}
      {expiring > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800">
          <AlertOctagon className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span><strong>{expiring} TMP{expiring !== 1 ? 's' : ''} expiring within 14 days.</strong> Contact the road authority to extend or re-submit before the expiry date. Works must cease if TMP lapses.</span>
        </div>
      )}
      {pending > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
          <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span><strong>{pending} TMP{pending !== 1 ? 's' : ''} awaiting road authority approval.</strong> Traffic control cannot be established until written approval is received.</span>
        </div>
      )}

      {/* New TMP form */}
      {showNew && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-blue-900">New Traffic Management Plan</h4>
            <button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Job number</label>
              <input value={newJobRef} onChange={e => setNewJobRef(e.target.value)} placeholder="TP-2026-005" className="input text-sm w-full" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Site address</label>
              <input value={newAddr} onChange={e => setNewAddr(e.target.value)} placeholder="Street, Suburb QLD" className="input text-sm w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">TMP class</label>
              <select value={newClass} onChange={e => setNewClass(e.target.value as TMPClass)} className="input text-sm w-full">
                <option value="SIMPLE">Simple — shoulder, low volume</option>
                <option value="STANDARD">Standard — 60km/h, lane closure</option>
                <option value="COMPLEX">Complex — 40km/h, arterial road</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Road authority</label>
              <select value={newAuthority} onChange={e => setNewAuthority(e.target.value as RoadAuthority)} className="input text-sm w-full">
                <option value="TMR">TMR (state road)</option>
                <option value="COUNCIL">Council (local road)</option>
                <option value="TRANSURBAN">Transurban (toll road)</option>
                <option value="PORT">Port authority</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Posted / working speed (km/h)</label>
              <div className="flex gap-2">
                <input type="number" value={newPosted}  onChange={e => setNewPosted(+e.target.value)}  className="input text-sm w-full" placeholder="60" />
                <input type="number" value={newWorking} onChange={e => setNewWorking(+e.target.value)} className="input text-sm w-full" placeholder="40" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">TC officers required</label>
              <input type="number" value={newTC} onChange={e => setNewTC(+e.target.value)} min={1} max={8} className="input text-sm w-full" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" id="night" checked={newNight} onChange={e => setNewNight(e.target.checked)} className="rounded" />
              <label htmlFor="night" className="text-sm text-gray-700">Night works required</label>
            </div>
          </div>
          <div className={`rounded-lg px-3 py-2 text-xs ${CLASS_META[newClass].bg} ${CLASS_META[newClass].text}`}>
            <strong>{newClass}:</strong> {CLASS_META[newClass].desc}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={handleCreate} disabled={!newJobRef.trim() || !newAddr.trim()}
              className="btn-primary text-sm disabled:opacity-40">Create TMP</button>
          </div>
        </div>
      )}

      {/* TMP list + detail */}
      <div className="flex gap-5 items-start">
        <div className={`${selected ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {tmps.map(tmp => {
            const sm  = STATUS_META[tmp.status]
            const cm  = CLASS_META[tmp.class]
            const pct = Math.round(tmp.sections.filter(s => s.complete).length / tmp.sections.length * 100)
            const sel = selected?.id === tmp.id
            const isExpiring = tmp.validTo && new Date(tmp.validTo) <= new Date(Date.now() + 14*86400000) && tmp.status === 'ACTIVE'

            return (
              <div key={tmp.id}
                onClick={() => setSelected(sel ? null : tmp)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  sel              ? 'border-orange-400 ring-2 ring-orange-100'
                  : isExpiring     ? 'border-red-300'
                  : tmp.status === 'PENDING_APPROVAL' ? 'border-amber-300'
                  : tmp.status === 'ACTIVE'           ? 'border-blue-300'
                  : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{tmp.tmpNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cm.bg} ${cm.text}`}>{tmp.class}</span>
                      {isExpiring && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">EXPIRING</span>}
                    </div>
                    <div className="font-semibold text-gray-900 text-sm truncate">{tmp.jobDescription}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3 flex-shrink-0" />{tmp.siteAddress}
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500 flex-wrap">
                      <span>{tmp.postedSpeed}→{tmp.workingSpeed} km/h</span>
                      <span>{tmp.tcOfficerCount} TC officer{tmp.tcOfficerCount !== 1 ? 's' : ''}</span>
                      {tmp.nightWorks && <span className="text-purple-600">Night works</span>}
                      <span className={pct === 100 ? 'text-green-600 font-medium' : pct < 50 ? 'text-red-600 font-medium' : 'text-amber-600 font-medium'}>
                        {pct}% complete
                      </span>
                      {tmp.validTo && tmp.status === 'ACTIVE' && <span>Valid to {fmt(tmp.validTo)}</span>}
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
                      <div className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                        style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${sel ? 'text-orange-500' : 'text-gray-400'}`} />
                </div>
              </div>
            )
          })}
          {tmps.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">
              No TMPs — create one for each job with roadside works
            </div>
          )}
        </div>

        {selected && (
          <div className="w-1/2 sticky top-6" style={{ maxHeight: 'calc(100vh - 160px)' }}>
            <TMPDetail
              tmp={selected}
              onClose={() => setSelected(null)}
              canEdit={canEdit}
              canApprove={canApprove}
              onSubmit={() => handleSubmit(selected.id)}
              onApprove={(ref) => handleApprove(selected.id, ref)}
              onActivate={() => handleActivate(selected.id)}
              onAmend={() => handleAmend(selected.id)}
            />
          </div>
        )}
      </div>

    </div>
  )
}
